"use client";

import { Warning2 } from "iconsax-reactjs";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePublicClient } from "wagmi";
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "@/components/ui";
import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";

export const RPCErrorAlert = () => {
  const t = useTranslations("modals");
  const [isVisible, setIsVisible] = useState(false);
  const isDismissedRef = useRef(false);
  // Explicit chainId so we always poll the chain the app *expects* to be on,
  // regardless of which network the user's wallet is currently switched to.
  // Without this, a wallet on Polygon mainnet 137 (or no wallet at all) would
  // make the alert silent even when the local fork RPC is wedged.
  const publicClient = usePublicClient({ chainId: EXPECTED_CHAIN_ID });

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    isDismissedRef.current = true;
  }, []);

  useEffect(() => {
    if (!publicClient) return;

    const checkRPCHealth = async () => {
      try {
        // Try a simple RPC call to check if the connection is working
        await publicClient.getBlockNumber();
        // If successful, hide the alert and reset dismissed state
        // so it can show again if a new error occurs later
        setIsVisible(false);
        isDismissedRef.current = false;
      } catch (error) {
        // If user dismissed the alert, don't show it again until RPC recovers
        if (isDismissedRef.current) {
          return;
        }

        // Check if it's an RPC error
        const errorMessage =
          error instanceof Error
            ? error.message.toLowerCase()
            : String(error).toLowerCase();

        const isRPCError =
          errorMessage.includes("failed to fetch") ||
          errorMessage.includes("network error") ||
          errorMessage.includes("timeout") ||
          errorMessage.includes("connection") ||
          errorMessage.includes("rpc") ||
          errorMessage.includes("provider") ||
          errorMessage.includes("network request failed") ||
          errorMessage.includes("could not detect network");

        if (isRPCError) {
          setIsVisible(true);
        }
      }
    };

    // Check immediately
    checkRPCHealth();

    // Set up periodic health checks every 10 seconds
    const interval = setInterval(checkRPCHealth, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [publicClient]);

  if (!isVisible) return null;

  return (
    // Kept in normal flow: as `absolute` it was lifted out of the layout and
    // painted straight over the page title and copy beneath it. Every layout
    // renders this before {children}, so flowing normally puts it above the
    // title and pushes the content down instead of covering it.
    <div className="w-full px-4 mb-4">
      <Alert className="w-full bg-semantic-error/10" colors={"error"}>
        <AlertContent>
          <div className="flex gap-2">
            <AlertIcon>
              <Warning2
                variant="Bold"
                className="text-semantic-error-foreground"
              />
            </AlertIcon>
            <div>
              <AlertTitle className="text-semantic-error-foreground">
                {t("rpcError.title")}
              </AlertTitle>
              <AlertDescription className="text-semantic-error-foreground">
                {t("rpcError.message")}{" "}
              </AlertDescription>
            </div>
            <XIcon
              className="text-[#920B3A] cursor-pointer"
              onClick={handleDismiss}
            />
          </div>
        </AlertContent>
      </Alert>
    </div>
  );
};
